import { IconButton, Menu, MenuItem } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useState } from 'react';
import {
  AllCommentsGenericDocument,
  AllCommentsGenericQuery,
  useDeleteCommentMutation,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { extractErrorMessageFromUnknownError } from '@/utils/error';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useAtomValue } from 'jotai';
import * as kpiState from '../../state';
import { LogTemplateType } from '@/__generated__/graphql';

type Props = {
  comment: NonNullable<AllCommentsGenericQuery['allComments']>[number] | null;
  onEdit?: () => void;
  onDelete?: () => void;
};

export const CommentOptionsMenu = ({ comment, onEdit, onDelete }: Props) => {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const menuOpen = Boolean(menuAnchorEl);

  const logType = useAtomValue(kpiState.selectedLogTemplateType);
  const structureId = useGetStructureId();
  const { showSnackBar } = useSnackBarMessage({});

  const [deleteComment, { loading }] = useDeleteCommentMutation({
    awaitRefetchQueries: true,
    refetchQueries: [AllCommentsGenericDocument],
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit?.();
  };

  const handleDelete = async () => {
    if (!comment) {
      showSnackBar('No comment to delete', 'error');
      return;
    }

    try {
      await deleteComment({
        variables: {
          ...(logType === LogTemplateType.WorklogTemplate && { structureId }),
          commentId: comment.id,
        },
      });

      onDelete?.();
    } catch (error: unknown) {
      const message = extractErrorMessageFromUnknownError(error);
      showSnackBar(message, 'error');
    } finally {
      handleMenuClose();
    }
  };

  return (
    <>
      <IconButton onClick={handleMenuOpen} sx={{ ml: 'auto' }}>
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={menuAnchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: '7vw',
            },
          },
        }}
      >
        <MenuItem onClick={handleEdit} disabled={loading || !comment}>
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} disabled={loading || !comment}>
          Delete
        </MenuItem>
      </Menu>
    </>
  );
};
